import type { ProductCard } from "@raumagarn/formatters";
import { VARIANTS_PER_DISPLAY, WISHLIST } from "~/config";
import { usePersistedStore } from "~/lib/stateManagement/persistedState/persistedStore";

export type ProductCardWishlist = {
	active: boolean;
	toggle: () => void;
};

type SingleVariantCard = {
	id: string;
	wishlist?: ProductCardWishlist;
	[index: string]: unknown;
};

type MultiVariantCard = {
	default: string;
	variants: {
		[index: string]: {
			id?: string;
			wishlist?: ProductCardWishlist;
			[index: string]: unknown;
		};
	};
};

type UseWishlist = typeof useWishlistLocal;
type AddWishlist = (products: ProductCard[]) => ProductCard[];

const variantCardWishlistBuilder =
	<T extends SingleVariantCard[]>(
		includes: (id: string) => boolean,
		toggle: (id: string) => void,
	) =>
	(products: T) =>
		products.map((product) => ({
			...product,
			wishlist: {
				active: includes(product.id),
				toggle: () => toggle(product.id),
			},
		}));

const productCardWishlistBuilder =
	<T extends MultiVariantCard[]>(includes: (id: string) => boolean, toggle: (id: string) => void) =>
	(products: T) =>
		products.map((product) => {
			Object.keys(product.variants).forEach((variant) => {
				const v = product.variants[variant];
				if (v?.id) {
					v.wishlist = {
						active: includes(v.id),
						toggle: () => toggle(v.id ?? ""),
					};
				}
			});
			return product;
		});

const useWishlistLocal = (productId?: string | undefined) => {
	const [ids, removedIds, add, remove, restoreLastRemoved, clearAll, clearRemoved, toggle] =
		usePersistedStore((store) => [
			store.wishlistIds,
			store.wishlistRemoved,
			store.wishlistAddId,
			store.wishlistRemoveId,
			store.wishlistRestoreLastRemoved,
			store.wishlistClearAll,
			store.wishlistClearRemoved,
			store.wishlistToggleId,
		]);

	const includes = (id: string) => ids.includes(id);

	const addWishlistToVariantCards = variantCardWishlistBuilder(includes, toggle);

	const addWishlistToProductCards = productCardWishlistBuilder(includes, toggle);

	return {
		ids,
		removedIds,
		add: productId ? () => add(productId) : () => {},
		remove: productId ? () => remove(productId) : () => {},
		restoreLastRemoved,
		clearAll,
		clearRemoved,
		toggle: () => (productId ? toggle(productId) : () => {}),
		isIncluded: () => (productId ? includes(productId) : void 0),
		addWishlistToVariantCards,
		addWishlistToProductCards:
			VARIANTS_PER_DISPLAY === "multi"
				? (addWishlistToProductCards as unknown as AddWishlist)
				: (addWishlistToVariantCards as unknown as AddWishlist),
	};
};

const useWishlistCentra = () => {
	return {
		ids: [],
		removedIds: [],
		add: () => {},
		remove: () => {},
		restoreLastRemoved: () => {},
		clearAll: () => {},
		clearRemoved: () => {},
		toggle: () => {},
		isIncluded: () => false,
		addWishlistToVariantCards: () => [],
		addWishlistToProductCards: () => [],
	};
};

export const useWishlist =
	WISHLIST === "centra" ? (useWishlistCentra as UseWishlist) : useWishlistLocal;
